import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError, FILE_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import EmptyState from "@/components/EmptyState";
import { XCircle, ArrowClockwise, Trash, MagnifyingGlass, ArrowSquareOut, CheckCircle } from "@phosphor-icons/react";

const fmt = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(n || 0);
const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return "—";
        const p = (n) => String(n).padStart(2, "0");
        return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
    }
    catch { return iso; }
};

export default function RejectedSlips() {
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [parties, setParties] = useState([]);
    const [filterParty, setFilterParty] = useState("all");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [query, setQuery] = useState("");
    const [confirmDel, setConfirmDel] = useState(null); // upload obj
    const [busyId, setBusyId] = useState(null);

    const load = useCallback(async () => {
        const params = { status: "rejected" };
        if (filterParty !== "all") params.party_id = filterParty;
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
        const [r1, r2] = await Promise.all([
            api.get("/uploads", { params }),
            api.get("/parties"),
        ]);
        setRows(r1.data);
        setParties(r2.data);
    }, [filterParty, dateFrom, dateTo]);

    useEffect(() => { load(); }, [load]);

    const partyName = useMemo(() => {
        const m = new Map(parties.map((p) => [p.id, p.name]));
        return (id) => m.get(id) || (id ? "(deleted party)" : "—");
    }, [parties]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter((r) =>
            (r.utr || "").toLowerCase().includes(q) ||
            partyName(r.party_id).toLowerCase().includes(q) ||
            (r.verification_notes || "").toLowerCase().includes(q)
        );
    }, [rows, query, partyName]);

    const totals = useMemo(() => ({
        count: filtered.length,
        amount: filtered.reduce((s, r) => s + Number(r.amount || 0), 0),
    }), [filtered]);

    const openInDrawer = (u) => {
        // Uploads page reads ?utr= and auto-opens the drawer on the matching slip
        const p = new URLSearchParams({ status: "rejected" });
        if (u.utr) p.set("utr", u.utr);
        else p.set("party_id", u.party_id);
        navigate(`/admin/uploads?${p.toString()}`);
    };

    const restore = async (u) => {
        if (!window.confirm(`Move this rejected slip back to Pending for re-verification?\n\nUTR: ${u.utr || "—"}\nAmount: ${fmt(u.amount)}`)) return;
        setBusyId(u.id);
        try {
            await api.post(`/uploads/${u.id}/restore`);
            toast.success("Slip restored to pending queue");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusyId(null); }
    };

    const acceptRejected = async (u) => {
        if (!window.confirm(`Accept this rejected slip and record it as a VERIFIED payment?\n\nUTR: ${u.utr || "—"}\nAmount: ${fmt(u.amount)}\n\nThis will post a payment + commission entry to ${u.party_id ? "the party's" : "the assigned party's"} ledger.`)) return;
        setBusyId(u.id);
        try {
            await api.post(`/uploads/${u.id}/accept-rejected`);
            toast.success("Slip accepted — ledger updated");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusyId(null); }
    };

    const del = async () => {
        if (!confirmDel) return;
        setBusyId(confirmDel.id);
        try {
            await api.delete(`/uploads/${confirmDel.id}`);
            toast.success("Slip permanently deleted");
            setConfirmDel(null);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusyId(null); }
    };

    return (
        <div className="space-y-6" data-testid="rejected-slips-page">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-rose-700">Cleanup</div>
                    <h1 className="mt-1 font-display text-4xl font-bold tracking-tight text-slate-900">Failed / Rejected Slips</h1>
                    <div className="mt-1 text-sm text-slate-500">
                        Curated view of slips that failed verification. Restore back to Pending, re-open for re-verify, or permanently delete.
                    </div>
                </div>
                <div className="flex flex-col items-end text-right">
                    <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Rejected · in view</div>
                    <div className="mt-1 font-display text-2xl font-bold tabular-nums text-rose-700" data-testid="rejected-count">
                        {totals.count}
                    </div>
                    <div className="text-xs tabular-nums text-slate-500" data-testid="rejected-total-amount">
                        {fmt(totals.amount)} disputed
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
                    <div>
                        <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">Party</Label>
                        <Select value={filterParty} onValueChange={setFilterParty}>
                            <SelectTrigger data-testid="rej-filter-party" className="mt-1"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All parties</SelectItem>
                                {parties.filter((p) => p.active !== false).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">From date</Label>
                        <Input data-testid="rej-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="mt-1" />
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">To date</Label>
                        <Input data-testid="rej-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="mt-1" />
                    </div>
                    <div className="sm:col-span-1">
                        <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">Search</Label>
                        <div className="relative mt-1">
                            <MagnifyingGlass size={14} className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                            <Input data-testid="rej-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="UTR, party, note…" className="pl-7" />
                        </div>
                    </div>
                    <div className="flex items-end">
                        <Button
                            data-testid="rej-clear-btn"
                            variant="outline"
                            onClick={() => { setFilterParty("all"); setDateFrom(""); setDateTo(""); setQuery(""); }}
                            className="w-full"
                        >
                            Clear
                        </Button>
                    </div>
                </div>
            </div>

            {filtered.length === 0 ? (
                <EmptyState
                    icon={XCircle}
                    title="No rejected slips"
                    description={rows.length === 0
                        ? "Great — nothing failed verification with the current filters."
                        : "No rejected slips match your search. Try clearing filters."}
                />
            ) : (
                <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="rejected-table">
                            <thead>
                                <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                                    <th className="px-5 py-3 font-medium">Uploaded</th>
                                    <th className="px-5 py-3 font-medium">Party</th>
                                    <th className="px-5 py-3 font-medium">UTR</th>
                                    <th className="px-5 py-3 text-right font-medium">Amount</th>
                                    <th className="px-5 py-3 font-medium">Reason / Note</th>
                                    <th className="px-5 py-3 font-medium">Rejected by</th>
                                    <th className="px-5 py-3 text-right font-medium">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((u) => (
                                    <tr key={u.id} data-testid={`rejected-row-${u.id}`} className="border-t border-slate-100 hover:bg-rose-50/40">
                                        <td className="px-5 py-3 text-slate-600 whitespace-nowrap">{fmtDate(u.uploaded_at)}</td>
                                        <td className="px-5 py-3">
                                            <div className="font-medium text-slate-900">{partyName(u.party_id)}</div>
                                            <div className="text-xs text-slate-500">{u.uploaded_by || "—"}</div>
                                        </td>
                                        <td className="px-5 py-3 font-mono text-xs text-slate-700">{u.utr || "—"}</td>
                                        <td className="px-5 py-3 text-right tabular-nums text-slate-900">{fmt(u.amount)}</td>
                                        <td className="px-5 py-3 text-slate-600">
                                            <div className="max-w-[24rem] whitespace-pre-line text-xs">
                                                {u.verification_notes || <span className="text-slate-400">—</span>}
                                            </div>
                                        </td>
                                        <td className="px-5 py-3 text-xs text-slate-500">
                                            <div>{u.verified_by || "—"}</div>
                                            <div className="text-slate-400">{u.verified_at ? (() => { const d = new Date(u.verified_at); const p = (n) => String(n).padStart(2, "0"); return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()}`; })() : ""}</div>
                                        </td>
                                        <td className="px-5 py-3">
                                            <div className="flex items-center justify-end gap-1">
                                                {u.file_url && (
                                                    <a
                                                        data-testid={`rejected-open-slip-${u.id}`}
                                                        href={`${FILE_BASE}${u.file_url}`}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-800"
                                                        title="Open slip file"
                                                    >
                                                        <ArrowSquareOut size={16} />
                                                    </a>
                                                )}
                                                <button
                                                    data-testid={`rejected-review-${u.id}`}
                                                    onClick={() => openInDrawer(u)}
                                                    className="rounded-md border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
                                                    title="Open in verification drawer"
                                                >
                                                    Review
                                                </button>
                                                <button
                                                    data-testid={`rejected-accept-${u.id}`}
                                                    disabled={busyId === u.id}
                                                    onClick={() => acceptRejected(u)}
                                                    className="flex items-center gap-1 rounded-md border border-emerald-300 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-800 hover:bg-emerald-100 disabled:opacity-50"
                                                    title="Accept & verify — post to ledger directly"
                                                >
                                                    <CheckCircle size={13} weight="fill" /> Accept
                                                </button>
                                                <button
                                                    data-testid={`rejected-restore-${u.id}`}
                                                    disabled={busyId === u.id}
                                                    onClick={() => restore(u)}
                                                    className="flex items-center gap-1 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-800 hover:bg-amber-100 disabled:opacity-50"
                                                    title="Move back to Pending queue"
                                                >
                                                    <ArrowClockwise size={13} /> Restore
                                                </button>
                                                <button
                                                    data-testid={`rejected-delete-${u.id}`}
                                                    disabled={busyId === u.id}
                                                    onClick={() => setConfirmDel(u)}
                                                    className="flex items-center gap-1 rounded-md border border-rose-300 bg-rose-50 px-2.5 py-1 text-xs font-semibold text-rose-700 hover:bg-rose-100 disabled:opacity-50"
                                                    title="Delete permanently"
                                                >
                                                    <Trash size={13} weight="fill" /> Delete
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Confirm delete dialog */}
            <Dialog open={!!confirmDel} onOpenChange={(v) => !v && setConfirmDel(null)}>
                <DialogContent data-testid="rejected-delete-dialog" className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-display flex items-center gap-2 text-rose-700">
                            <Trash size={20} weight="fill" /> Delete slip permanently?
                        </DialogTitle>
                        <DialogDescription className="text-sm text-slate-600">
                            This removes the slip record and its file from disk. The action cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    {confirmDel && (
                        <div className="space-y-1 rounded-md border border-rose-200 bg-rose-50/60 p-3 text-sm">
                            <div><span className="text-slate-500">Party:</span> <span className="font-medium">{partyName(confirmDel.party_id)}</span></div>
                            <div><span className="text-slate-500">UTR:</span> <span className="font-mono">{confirmDel.utr || "—"}</span></div>
                            <div><span className="text-slate-500">Amount:</span> <span className="tabular-nums font-semibold">{fmt(confirmDel.amount)}</span></div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" data-testid="rejected-delete-cancel" onClick={() => setConfirmDel(null)}>Cancel</Button>
                        <Button
                            data-testid="rejected-delete-confirm"
                            onClick={del}
                            disabled={busyId === confirmDel?.id}
                            className="bg-rose-600 text-white hover:bg-rose-700"
                        >
                            {busyId === confirmDel?.id ? "Deleting…" : "Delete permanently"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
